import type { CardRule, DashboardState } from './types';

/**
 * Business rules defining when each dashboard card should be displayed.
 * Rules are evaluated in priority order, with lower numbers appearing first.
 *
 * The conditions function receives the aggregated dashboard state and returns
 * a boolean indicating whether the card should be shown.
 */
export const DASHBOARD_RULES: CardRule[] = [
  // ========== TOP SECTION ==========

  // Appointments list - shows at top when user has upcoming appointments
  {
    id: 'appointments-list',
    conditions: state => state.hasUpcomingAppointments,
    priority: 0,
    slot: 'primary',
  },

  // ========== PRIMARY CARDS ==========

  // Record card - always visible
  {
    id: 'record',
    conditions: () => true,
    priority: 1,
    slot: 'primary',
  },

  // ========== ADAPTIVE NOTES SECTION ==========
  // Only one of these three cards shows based on user progress

  // Notes card - show when user hasn't created their first note
  // AND either still onboarding OR doesn't have upcoming appointments
  {
    id: 'notes',
    conditions: state =>
      !state.hasCreatedFirstNote && (state.showOnboarding || !state.hasUpcomingAppointments),
    priority: 2,
    slot: 'secondary',
  },

  // Schedule Appointment card - show when user has notes, still onboarding,
  // but NO upcoming appointments
  {
    id: 'schedule-appointment',
    conditions: state =>
      state.hasCreatedFirstNote && state.showOnboarding && !state.hasUpcomingAppointments,
    priority: 2,
    slot: 'secondary',
  },

  // Request Payment card - show when:
  // 1. User has notes, finished onboarding, and NO upcoming appointments
  // 2. OR user has notes, still onboarding, but HAS upcoming appointments
  {
    id: 'request-payment',
    conditions: state =>
      state.hasCreatedFirstNote &&
      ((!state.showOnboarding && !state.hasUpcomingAppointments) ||
        (state.showOnboarding && state.hasUpcomingAppointments)),
    priority: 2,
    slot: 'secondary',
  },

  // ========== PATIENT SECTION ==========

  // Patient card - visible when onboarding OR no upcoming appointments
  {
    id: 'patient',
    conditions: state => state.showOnboarding || !state.hasUpcomingAppointments,
    getProps: state => ({
      // This prop could control whether to show "Create First Patient" vs "Manage Patients"
      showCreateAction: !state.hasRealPatients,
    }),
    priority: 3,
    slot: 'primary',
  },

  // ========== ADAPTIVE ONBOARDING SECTION ==========
  // Only one of these two cards shows based on onboarding status

  // Onboarding card - show when user is still in onboarding
  {
    id: 'onboarding',
    conditions: state => state.showOnboarding,
    priority: 4,
    slot: 'onboarding',
  },

  // Upcoming Appointments card - show when onboarding is complete
  // AND no upcoming appointments in the appointments-list
  {
    id: 'upcoming-appointments',
    conditions: state => !state.showOnboarding && !state.hasUpcomingAppointments,
    priority: 4,
    slot: 'onboarding',
  },

  // ========== ADAPTIVE TOOLS SECTION ==========
  // Only one of these two cards shows based on note creation status

  // Admin Tools card - show when user hasn't created their first note
  {
    id: 'admin-tools',
    conditions: state => !state.hasCreatedFirstNote,
    priority: 5,
    slot: 'tools',
  },

  // Notes Review card - show when user has created notes
  {
    id: 'notes-review',
    conditions: state => state.hasCreatedFirstNote,
    priority: 5,
    slot: 'tools',
  },

  // ========== REFERRAL SECTION ==========

  // Share JotPsych card - visible at bottom for non-active subscribers
  // Active subscribers see the referral invite in the header instead
  {
    id: 'share-jotpsych',
    conditions: state => !state.isActiveSubscriber,
    priority: 100, // High number = appears at bottom
    slot: 'secondary',
  },
];

/**
 * Helper function to get rules for a specific slot
 */
export const getRulesBySlot = (slot: string): CardRule[] => {
  return DASHBOARD_RULES.filter(rule => rule.slot === slot);
};

/**
 * Helper function to get visible cards based on state
 */
export const getVisibleCards = (state: DashboardState): CardRule[] => {
  return DASHBOARD_RULES.filter(rule => rule.conditions(state));
};
